import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Button, Field, Input } from "@ternary/ui"
import { type FormEvent, useEffect, useRef, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { loadAdminSession, loginAdmin, requestAdminPasswordReset } from "../api.js"
import { SetupPage } from "../components/setup-page.js"
import { consentReturnPath } from "./admin-connect-page.js"

type LoginErrors = { email?: string | undefined; password?: string | undefined }

export function AdminLoginPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Claude's consent page sends people here to sign in, and back again after.
  const [search] = useSearchParams()
  const next = consentReturnPath(search.get("next")) ?? "/dashboard"
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [errors, setErrors] = useState<LoginErrors>({})
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const session = useQuery({
    queryFn: loadAdminSession,
    queryKey: ["admin-session"],
  })
  const login = useMutation({
    mutationFn: loginAdmin,
    onSuccess(result) {
      queryClient.clear()
      queryClient.setQueryData(["admin-session"], {
        authenticated: true,
        session: result.session,
      })
      navigate(next, { replace: true })
    },
  })
  const forgotPassword = useMutation({
    mutationFn: requestAdminPasswordReset,
  })

  useEffect(() => {
    if (session.data?.authenticated) {
      navigate(next, { replace: true })
    }
  }, [navigate, next, session.data])

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const next: LoginErrors = {
      email: email ? undefined : "Enter your email address.",
      password: password ? undefined : "Enter your password.",
    }
    setErrors(next)
    forgotPassword.reset()
    // The first field to fix takes focus, so its error is read out with it.
    if (next.email) emailRef.current?.focus()
    else if (next.password) passwordRef.current?.focus()
    else login.mutate({ email, password })
  }

  function requestPasswordReset() {
    login.reset()
    if (!email) {
      setErrors({ email: "Enter your email address first." })
      emailRef.current?.focus()
      return
    }
    setErrors({})
    forgotPassword.mutate({ email })
  }

  const error =
    login.error instanceof Error
      ? login.error.message
      : forgotPassword.error instanceof Error
        ? forgotPassword.error.message
        : null

  return (
    <SetupPage>
      <form className="admin-auth" noValidate onSubmit={submit}>
        <h1 className="tny-wizard-title">Sign in</h1>
        <Field error={errors.email} label="Email">
          <Input
            // The account's sign-in name, which is what a password manager fills.
            autoComplete="username"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="owner@northwind.example"
            ref={emailRef}
            type="email"
            value={email}
          />
        </Field>
        <Field error={errors.password} label="Password">
          <Input
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
            ref={passwordRef}
            type="password"
            value={password}
          />
        </Field>
        {/* A button, since it sends a request (ruling C21), drawn as a text link under Password. */}
        <button
          aria-busy={forgotPassword.isPending || undefined}
          className="admin-text-button admin-auth-forgot"
          disabled={forgotPassword.isPending}
          onClick={requestPasswordReset}
          type="button"
        >
          Forgot password?
        </button>
        {forgotPassword.data ? (
          <p className="admin-note" role="status">
            {forgotPassword.data.message}
          </p>
        ) : null}
        {error ? (
          <p className="tny-error" role="alert">
            {error}
          </p>
        ) : null}
        <Button loading={login.isPending} type="submit">
          Sign in
        </Button>
        <p className="admin-note admin-auth-switch">
          New to Ternary? <Link to="/register">Create an account</Link>
        </p>
      </form>
    </SetupPage>
  )
}
